import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { logger } from "@/lib/logger";
import { db } from '@/lib/firebase';
import {
  TeamOnboarding,
  UserOnboardingPrefs,
  OnboardingChecklistItem,
  defaultTeamOnboarding,
  defaultUserOnboardingPrefs,
} from '@/types/onboarding';

/**
 * Get team onboarding state
 */
export async function getTeamOnboarding(
  teamId: string
): Promise<TeamOnboarding | null> {
  try {
    const docRef = doc(db, 'teams', teamId, 'settings', 'onboarding');
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return docSnap.data() as TeamOnboarding;
  } catch (error) {
    logger.error('Error getting team onboarding:', error);
    throw error;
  }
}

/**
 * Initialize team onboarding state
 */
export async function initializeTeamOnboarding(
  teamId: string
): Promise<void> {
  try {
    const docRef = doc(db, 'teams', teamId, 'settings', 'onboarding');
    
    await setDoc(docRef, {
      ...defaultTeamOnboarding,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    logger.error('Error initializing team onboarding:', error);
    throw error;
  }
}

/**
 * Mark a checklist item as complete
 */
export async function markChecklistItem(
  teamId: string,
  item: OnboardingChecklistItem
): Promise<void> {
  try {
    const docRef = doc(db, 'teams', teamId, 'settings', 'onboarding');
    
    // Get current state
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      // Initialize if doesn't exist
      await initializeTeamOnboarding(teamId);
    }

    // Update the specific checklist item
    await updateDoc(docRef, {
      [`checklist.${item}`]: true,
    });

    // Check if all items are complete
    const updatedSnap = await getDoc(docRef);
    const data = updatedSnap.data() as TeamOnboarding;

    const allComplete = Object.values(data.checklist).every(
      (value) => value === true
    );

    // If all complete and not yet initialized, mark as initialized
    if (allComplete && !data.initializedAt) {
      await updateDoc(docRef, {
        initializedAt: serverTimestamp(),
      });
    }
  } catch (error) {
    logger.error('Error marking checklist item:', error);
    throw error;
  }
}

/**
 * Get user onboarding preferences
 */
export async function getUserOnboardingPrefs(
  userId: string
): Promise<UserOnboardingPrefs> {
  try {
    const docRef = doc(db, 'users', userId, 'private', 'onboarding');
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return defaultUserOnboardingPrefs;
    }

    return docSnap.data() as UserOnboardingPrefs;
  } catch (error) {
    logger.error('Error getting user onboarding prefs:', error);
    return defaultUserOnboardingPrefs;
  }
}

/**
 * Dismiss the first-run banner for a user
 */
export async function dismissFirstRunBanner(userId: string): Promise<void> {
  try {
    const docRef = doc(db, 'users', userId, 'private', 'onboarding');
    
    await setDoc(
      docRef,
      {
        bannerDismissedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (error) {
    logger.error('Error dismissing first-run banner:', error);
    throw error;
  }
}

/**
 * Check if team is in first-run state
 */
export function isFirstRun(onboarding: TeamOnboarding | null): boolean {
  if (!onboarding) return true;
  return onboarding.initializedAt === null || onboarding.initializedAt === undefined;
}

/**
 * Check if user has dismissed the banner
 */
export function hasDismissedBanner(prefs: UserOnboardingPrefs): boolean {
  return prefs.bannerDismissedAt !== null && prefs.bannerDismissedAt !== undefined;
}
